import { z } from "zod";

const ForwardMessageSchema = z.strictObject({
  id: z.string(),
  direction: z.enum(["inbound", "outbound"]),
  from: z.string(),
  to: z.array(z.string()),
  cc: z.array(z.string()),
  at: z.string(),
  subject: z.string(),
  html: z.string(),
  plainText: z.string(),
  attachments: z.array(
    z.strictObject({
      id: z.string(),
      name: z.string(),
      bytes: z.number().nonnegative().nullable(),
    }),
  ),
});

export const ForwardRecipientsSchema = z
  .strictObject({
    to: z.array(z.email().max(254)).min(1).max(50),
    cc: z.array(z.email().max(254)).max(50),
    bcc: z.array(z.email().max(254)).max(50),
  })
  .refine((sets) => {
    const addresses = [...sets.to, ...sets.cc, ...sets.bcc].map((value) => value.toLowerCase());
    return new Set(addresses).size === addresses.length;
  }, "Enter each recipient only once");
export type ForwardRecipients = z.infer<typeof ForwardRecipientsSchema>;

export const ForwardSourceSchema = z.strictObject({
  workspaceId: z.number().int().positive(),
  campaignId: z.number().int().positive(),
  leadId: z.number().int().positive(),
  senderEmailId: z.number().int().positive(),
  senderAddress: z.email(),
  providerReplyId: z.number().int().positive(),
  fingerprint: z.string().regex(/^[a-f0-9]{64}$/u),
  message: ForwardMessageSchema,
});
export type ForwardSource = z.infer<typeof ForwardSourceSchema>;

/** Full source content, separate from the reading frame's trimmed regions and private image URLs. */
export const ForwardCompositionSchema = z.strictObject({
  source: ForwardSourceSchema,
  localFingerprint: z.string().regex(/^[a-f0-9]{64}$/u),
  html: z.string().max(100_000),
  plainText: z.string().max(100_000),
  omittedImages: z.number().int().nonnegative(),
});
export type ForwardComposition = z.infer<typeof ForwardCompositionSchema>;

export const ForwardHistorySchema = z.array(
  z.strictObject({
    id: z.string(),
    sourceMessageId: z.string(),
    recipients: ForwardRecipientsSchema,
    state: z.enum(["queued", "claimed", "sent", "cancelled", "uncertain", "refused"]),
    deliveredSubject: z.string().nullable(),
  }),
);
